import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MessageCircle, Phone, Send, X, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import BaseUrl from '../ApiServices/BaseUrl';
import './GulfWorkerLeadBot.css';

const DEFAULT_BOT_AVATAR = '/temp/manager.jpg';
const BOT1_POSTER = '/temp/bot1.jpeg';

const CATEGORY_OPTIONS = [
  'Maid',
  'Driver',
  'Nurse',
  'Cook',
  'Nanny',
  'Other'
];

const NATIONALITY_OPTIONS = [
  'Philippines',
  'India',
  'Indonesia',
  'Sri Lanka',
  'Nepal',
  'Ethiopia',
  'Kenya',
  'Uganda',
  'Other'
];

const RELIGION_OPTIONS = [
  'Hindu',
  'Muslim',
  'Christian',
  'Any'
];

const DRIVING_LICENSE_OPTIONS = [
  'Qatar',
  'UAE',
  'Saudi',
  'Kuwait',
  'Oman',
  'Bahrain',
  'India',
  'Philippines',
  'Home Country Licence',
  'International Driving Permit',
  'Not Licensed',
  'Other'
];

const NURSE_LICENSE_STATUS_OPTIONS = [
  'Not Yet Started',
  'DataFlow Completed',
  'DataFlow In Process',
  'Prometric Completed',
  'Prometric In Process',
  'Prometric + DataFlow Completed'
];

const NURSING_LICENSE_COUNTRY_OPTIONS = [
  'Qatar - QCHP / DHP',
  'UAE - DHA / DOH / MOH',
  'Saudi Arabia - SCFHS',
  'Oman - MOH License',
  'Kuwait - MOH License',
  'Bahrain - NHRA',
  'Not Licensed',
  'Other'
];

const COUNTRY_CODES = [
  { code: '+93', country: 'Afghanistan', flag: '🇦🇫' },
  { code: '+355', country: 'Albania', flag: '🇦🇱' },
  { code: '+213', country: 'Algeria', flag: '🇩🇿' },
  { code: '+376', country: 'Andorra', flag: '🇦🇩' },
  { code: '+244', country: 'Angola', flag: '🇦🇴' },
  { code: '+973', country: 'Bahrain', flag: '🇧🇭' },
  { code: '+880', country: 'Bangladesh', flag: '🇧🇩' },
  { code: '+20', country: 'Egypt', flag: '🇪🇬' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+62', country: 'Indonesia', flag: '🇮🇩' },
  { code: '+962', country: 'Jordan', flag: '🇯🇴' },
  { code: '+965', country: 'Kuwait', flag: '🇰🇼' },
  { code: '+961', country: 'Lebanon', flag: '🇱🇧' },
  { code: '+212', country: 'Morocco', flag: '🇲🇦' },
  { code: '+968', country: 'Oman', flag: '🇴🇲' },
  { code: '+92', country: 'Pakistan', flag: '🇵🇰' },
  { code: '+63', country: 'Philippines', flag: '🇵🇭' },
  { code: '+974', country: 'Qatar', flag: '🇶🇦' },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
  { code: '+94', country: 'Sri Lanka', flag: '🇱🇰' },
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+44', country: 'United Kingdom', flag: '🇬🇧' },
  { code: '+1', country: 'United States', flag: '🇺🇸' },
];

const GulfWorkerLeadBot = ({ pageContext = 'Home' }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [showNudge, setShowNudge] = useState(true);
  const [showFirstVisitPoster, setShowFirstVisitPoster] = useState(false);
  const [isTriggerVisible, setIsTriggerVisible] = useState(true);
  const [isScrolledPastHero, setIsScrolledPastHero] = useState(window.innerWidth > 768);

  useEffect(() => {
    const handleScroll = () => {
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        setIsScrolledPastHero(window.scrollY > 280);
      } else {
        setIsScrolledPastHero(true);
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);
  const [messages, setMessages] = useState([]);
  const [step, setStep] = useState('category');
  const [input, setInput] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneCode, setPhoneCode] = useState('+974');
  const [category, setCategory] = useState('');
  const [experience, setExperience] = useState('');
  const [gender, setGender] = useState('');
  const [otherCategoryText, setOtherCategoryText] = useState('');
  const [nationality, setNationality] = useState('');
  const [religion, setReligion] = useState('');
  const [workType, setWorkType] = useState('');
  const [livingPreference, setLivingPreference] = useState('');
  const [timeline, setTimeline] = useState('');
  const [notes, setNotes] = useState('');
  const [drivingLicense, setDrivingLicense] = useState('');
  const [nurseLicenseStatus, setNurseLicenseStatus] = useState('');
  const [nursingLicenseCountry, setNursingLicenseCountry] = useState('');
  const [leadName, setLeadName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCodeDropdown, setShowCodeDropdown] = useState(false);
  const [searchPhoneCode, setSearchPhoneCode] = useState('');
  
  const messagesEndRef = useRef(null);
  const codeDropdownRef = useRef(null);
  const [typingCount, setTypingCount] = useState(0);
  const isTyping = typingCount > 0;

  const botName = 'Sara';

  const copy = {
    greeting: t('chatbot.greeting'),
    needHelp: t('chatbot.need_help'),
    askNationality: t('chatbot.ask_nationality'),
    askExperience: t('chatbot.ask_experience'),
    askGender: t('chatbot.ask_gender'),
    otherNationalityPrompt: t('chatbot.other_nationality_prompt'),
    askDrivingLicense: t('chatbot.ask_driving_license'),
    askNurseLicenseStatus: t('chatbot.ask_nurse_license_status'),
    askNursingLicenseCountry: t('chatbot.ask_nursing_license_country'),
    askWorkType: t('chatbot.ask_work_type'),
    askLiving: t('chatbot.ask_living'),
    askTimeline: t('chatbot.ask_timeline'),
    askNotes: t('chatbot.ask_notes'),
    askName: t('chatbot.ask_name'),
    askPhone: t('chatbot.ask_phone'),
    invalidCategory: t('chatbot.invalid_category'),
    invalidNationality: t('chatbot.invalid_nationality'),
    invalidName: t('chatbot.invalid_name'),
    invalidPhone: t('chatbot.invalid_phone'),
    askOtherCategory: t('chatbot.ask_other_category'),
    success: t('chatbot.success'),
    handoffIntro: t('chatbot.handoff_intro'),
    handoffProfiles: t('chatbot.handoff_profiles'),
    handoffButton: t('chatbot.handoff_button'),
    handoffThanks: t('chatbot.handoff_thanks'),
    submitError: t('chatbot.submit_error'),
    skip: t('chatbot.skip'),
    experienceOptions: [
      { id: 'New (0–1 year)', label: t('chatbot.experience_options.New (0–1 year)') },
      { id: '2–4 years', label: t('chatbot.experience_options.2–4 years') },
      { id: '5+ years', label: t('chatbot.experience_options.5+ years') },
    ],
    genderOptions: [
      { id: 'Female', label: t('chatbot.gender_options.Female') },
      { id: 'Male', label: t('chatbot.gender_options.Male') },
      { id: 'No preference', label: t('chatbot.gender_options.No preference') },
    ],
    workTypeOptions: [
      { id: 'Full-time', label: t('chatbot.work_types.Full-time') },
      { id: 'Part-time', label: t('chatbot.work_types.Part-time') }
    ],
    livingOptions: [
      { id: 'Live-in', label: t('chatbot.living_options.Live-in') },
      { id: 'Live-out', label: t('chatbot.living_options.Live-out') }
    ],
    timelineOptions: [
      { id: 'Immediately', label: t('chatbot.timeline_options.Immediately') },
      { id: 'Within 1 week', label: t('chatbot.timeline_options.Within 1 week') },
      { id: 'Within 1 month', label: t('chatbot.timeline_options.Within 1 month') }
    ]
  };

  const religionPrompt = t('chatbot.religion_prompt');

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOpen]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setTypingCount(prev => prev + 1);
      const timer = setTimeout(() => {
        setMessages([{ role: 'bot', content: copy.greeting }]);
        setTypingCount(prev => Math.max(0, prev - 1));
        setStep('category');
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isOpen, copy.greeting, messages.length]);

  useEffect(() => {
    const handler = (e) => {
      if (codeDropdownRef.current && !codeDropdownRef.current.contains(e.target)) {
        setShowCodeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const nudgeTimer = setTimeout(() => setShowNudge(false), 9000);
    return () => clearTimeout(nudgeTimer);
  }, [isOpen, isDismissed]);

  useEffect(() => {
    const visibilityTimer = setTimeout(() => setIsTriggerVisible(true), 50000);
    return () => clearTimeout(visibilityTimer);
  }, []);

  const addBotMessage = (content, handoff, onComplete) => {
    if (!content && !handoff) {
      onComplete?.();
      return;
    }
    setTypingCount(prev => prev + 1);
    setTimeout(() => {
      setMessages((prev) => [...prev, { role: 'bot', content, handoff }]);
      setTypingCount(prev => Math.max(0, prev - 1));
      onComplete?.();
    }, 1200);
  };

  const addUserMessage = (content) => {
    setMessages((prev) => [...prev, { role: 'user', content }]);
  };

  const handleCategorySelect = (value) => {
    setCategory(value);
    addUserMessage(t("chatbot.categories." + value));
    if (value === 'Other') {
      setTimeout(() => {
        addBotMessage(copy.askOtherCategory, undefined, () => setStep('other'));
      }, 450);
      return;
    }
    if (value === 'Driver') {
      setTimeout(() => {
        addBotMessage(copy.askDrivingLicense, undefined, () => setStep('driving_license'));
      }, 450);
      return;
    }
    if (value === 'Nurse') {
      setTimeout(() => {
        addBotMessage(copy.askNurseLicenseStatus, undefined, () => setStep('nurse_license_status'));
      }, 450);
      return;
    }
    setTimeout(() => {
      addBotMessage(copy.askExperience, undefined, () => setStep('experience'));
    }, 450);
  };

  const handleDrivingLicenseSelect = (value) => {
    addUserMessage(t("chatbot.driving_licenses." + value));
    if (value === 'Other') {
      setTimeout(() => {
        addBotMessage(t('chatbot.ask_driving_license_other'), undefined, () => setStep('driving_license_other'));
      }, 450);
      return;
    }
    setDrivingLicense(value);
    setTimeout(() => {
      addBotMessage(copy.askExperience, undefined, () => setStep('experience'));
    }, 450);
  };

  const handleNurseLicenseStatusSelect = (value) => {
    setNurseLicenseStatus(value);
    addUserMessage(t("chatbot.nurse_license_status." + value));
    if (value === 'Not Yet Started') {
      setTimeout(() => {
        addBotMessage(copy.askExperience, undefined, () => setStep('experience'));
      }, 450);
      return;
    }
    setTimeout(() => {
      addBotMessage(copy.askNursingLicenseCountry, undefined, () => setStep('nursing_license_country'));
    }, 450);
  };

  const handleNursingLicenseCountrySelect = (value) => {
    addUserMessage(t("chatbot.nursing_license_countries." + value));
    if (value === 'Other') {
      setTimeout(() => {
        addBotMessage(t('chatbot.ask_nursing_license_country_other'), undefined, () => setStep('nursing_license_country_other'));
      }, 450);
      return;
    }
    setNursingLicenseCountry(value);
    setTimeout(() => {
      addBotMessage(copy.askExperience, undefined, () => setStep('experience'));
    }, 450);
  };

  const handleExperienceSelect = (id, label) => {
    setExperience(id);
    addUserMessage(label);
    setTimeout(() => {
      addBotMessage(copy.askGender, undefined, () => setStep('gender'));
    }, 450);
  };

  const handleGenderSelect = (id, label) => {
    setGender(id);
    addUserMessage(label);
    setTimeout(() => {
      addBotMessage(copy.askNationality, undefined, () => setStep('nationality'));
    }, 450);
  };

  const handleNationalitySelect = (value) => {
    addUserMessage(t("chatbot.nationalities." + value));
    if (value === 'Other') {
      setTimeout(() => {
        addBotMessage(copy.otherNationalityPrompt, undefined, () => setStep('nationality_other'));
      }, 450);
      return;
    }
    setNationality(value);
    setTimeout(() => {
      addBotMessage(religionPrompt, undefined, () => setStep('religion'));
    }, 450);
  };

  const handleReligionSelect = (value) => {
    setReligion(value);
    addUserMessage(t("chatbot.religions." + value));
    setTimeout(() => {
      addBotMessage(copy.askWorkType, undefined, () => setStep('work_type'));
    }, 450);
  };

  const handleWorkTypeSelect = (id, label) => {
    setWorkType(id);
    addUserMessage(label);
    setTimeout(() => {
      addBotMessage(copy.askLiving, undefined, () => setStep('living'));
    }, 450);
  };

  const handleLivingSelect = (id, label) => {
    setLivingPreference(id);
    addUserMessage(label);
    setTimeout(() => {
      addBotMessage(copy.askTimeline, undefined, () => setStep('timeline'));
    }, 450);
  };

  const handleTimelineSelect = (id, label) => {
    setTimeline(id);
    addUserMessage(label);
    addBotMessage(copy.askName);
    setStep('name');
  };

  const handleTextSubmit = () => {
    const text = input.trim();
    if (!text) return;

    if (step === 'other') {
      if (text.length < 2) { addBotMessage(copy.invalidCategory); return; }
      addUserMessage(text);
      setOtherCategoryText(text);
      setInput('');
      setTimeout(() => { addBotMessage(copy.askExperience); setStep('experience'); }, 450);
    } else if (step === 'driving_license_other') {
      if (text.length < 2) { addBotMessage(t('chatbot.invalid_country')); return; }
      addUserMessage(text);
      setDrivingLicense(text);
      setInput('');
      setTimeout(() => { addBotMessage(copy.askExperience); setStep('experience'); }, 450);
    } else if (step === 'nursing_license_country_other') {
      if (text.length < 2) { addBotMessage(t('chatbot.invalid_country')); return; }
      addUserMessage(text);
      setNursingLicenseCountry(text);
      setInput('');
      setTimeout(() => { addBotMessage(copy.askExperience); setStep('experience'); }, 450);
    } else if (step === 'nationality_other') {
      if (text.length < 2) { addBotMessage(copy.invalidNationality); return; }
      addUserMessage(text);
      setNationality(text);
      setInput('');
      setTimeout(() => { addBotMessage(religionPrompt); setStep('religion'); }, 450);
    } else if (step === 'notes') {
      addUserMessage(text);
      setNotes(text);
      setInput('');
      submitLead(text);
    } else if (step === 'name') {
      if (text.length < 2) { addBotMessage(copy.invalidName); return; }
      addUserMessage(text);
      setLeadName(text);
      setInput('');
      setTimeout(() => { addBotMessage(copy.askPhone); setStep('phone'); }, 450);
    }
  };

  const submitLead = async (finalNotes) => {
    const cleanPhone = phoneNumber.replace(/\s+/g, '');
    if (step === 'phone' && cleanPhone.length < 6) {
      addBotMessage(copy.invalidPhone);
      return;
    }

    setIsSubmitting(true);
    setStep('submitting');

    try {
      const payload = {
        name: leadName,
        phone: cleanPhone,
        phoneCountryCode: phoneCode,
        category,
        otherCategoryText,
        experience,
        gender,
        nationality,
        religion,
        workType,
        livingPreference,
        timeline,
        notes: finalNotes || notes,
        drivingLicense,
        nurseLicenseStatus,
        nursingLicenseCountry,
        pageContext,
        pagePath: window.location.pathname,
        source: 'lead_bot'
      };

      const response = await axios.post(`${BaseUrl}/api/lead-bot/submit`, payload);
      const assignedAgency = response.data?.data?.assignedAgency;

      addBotMessage(copy.success.replace('{name}', leadName));
      if (assignedAgency?.contactNumber) {
        addBotMessage('', {
          contactNumber: assignedAgency.contactNumber,
          profilesUrl: assignedAgency.profilesUrl,
          displayName: assignedAgency.displayName || ''
        });
      }
      setStep('done');
    } catch (error) {
      console.error('Error submitting lead:', error);
      addBotMessage(copy.submitError);
      setStep('phone');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetChat = () => {
    setMessages([]);
    setStep('category');
    setInput('');
    setPhoneNumber('');
    setCategory('');
    setOtherCategoryText('');
    setExperience('');
    setGender('');
    setNationality('');
    setReligion('');
    setWorkType('');
    setLivingPreference('');
    setTimeline('');
    setNotes('');
    setDrivingLicense('');
    setNurseLicenseStatus('');
    setNursingLicenseCountry('');
    setLeadName('');
    setIsOpen(false);
    setIsDismissed(true);
    setShowNudge(false);
  };

  const handleOpenChat = () => {
    setShowFirstVisitPoster(false);
    setIsOpen(true);
    setIsDismissed(false);
    setShowNudge(false);
  };

  const renderMessageContent = (content) => {
    if (!content) return null;
    const parts = content.split(/(WhatsApp number|name|رقم الواتساب|اسمك)/gi);
    return parts.map((part, i) =>
      /(WhatsApp number|name|رقم الواتساب|اسمك)/i.test(part) ? (
        <span key={i} className="gw-bot-highlight">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const showStepOptions = !isTyping;

  return (
    <div className={`gw-bot-wrapper ${isScrolledPastHero ? 'gw-bot-scrolled' : ''}`}>
      {!isOpen && isTriggerVisible && isScrolledPastHero && (
        <div className="gw-bot-trigger-container">
          <button
            onClick={handleOpenChat}
            className="gw-bot-fab avatar"
            aria-label="Open Chatbot"
          >
            <span className="gw-bot-fab-border-1" />
            <span className="gw-bot-fab-border-2" />
            <img
              src={DEFAULT_BOT_AVATAR}
              alt={botName}
              className="gw-bot-fab-img"
            />
          </button>
        </div>
      )}

      {isOpen && (
        <div className="gw-bot-window">
          <div className="gw-bot-header">
            <div className="gw-bot-header-info">
              <div className="gw-bot-header-avatar">
                <img src={DEFAULT_BOT_AVATAR} alt={botName} />
              </div>
              <div className="gw-bot-header-text">
                <h3>{t('chatbot.header_greeting', { botName })}</h3>
                <div className="gw-bot-status-indicator">
                  <div className="gw-bot-dot" />
                  <span>{t('chatbot.online')}</span>
                </div>
              </div>
            </div>
            <button onClick={resetChat} className="gw-bot-close-btn">
              <X size={20} />
            </button>
          </div>

          <div className="gw-bot-messages-container">
            {messages.map((msg, idx) => (
              <div key={idx} className={`gw-bot-msg-row ${msg.role === 'user' ? 'user' : 'bot'}`}>
                {msg.role === 'bot' && (
                  <div className="gw-bot-msg-avatar">
                    <img src={DEFAULT_BOT_AVATAR} alt={botName} />
                  </div>
                )}
                <div className="gw-bot-bubble">
                  {msg.handoff ? (
                    <div className="gw-bot-handoff">
                      <div className="gw-bot-handoff-title">{copy.handoffIntro}</div>
                      <div className="gw-bot-handoff-actions">
                        <a
                          href={`https://wa.me/${msg.handoff.contactNumber.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="gw-bot-handoff-btn whatsapp"
                        >
                          <MessageCircle size={16} /> WhatsApp
                        </a>
                        <a
                          href={`tel:${msg.handoff.contactNumber}`}
                          className="gw-bot-handoff-btn call"
                        >
                          <Phone size={16} /> Call
                        </a>
                      </div>
                      <div className="gw-bot-handoff-title" style={{ marginTop: '12px' }}>{copy.handoffProfiles}</div>
                      <a
                        href={msg.handoff.profilesUrl}
                        className="gw-bot-handoff-btn"
                        style={{ border: '1px solid #10b981', color: '#10b981' }}
                      >
                        🔗 {copy.handoffButton}
                      </a>
                    </div>
                  ) : (
                    renderMessageContent(msg.content)
                  )}
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="gw-bot-msg-row bot">
                <div className="gw-bot-msg-avatar">
                  <img src={DEFAULT_BOT_AVATAR} alt={botName} />
                </div>
                <div className="gw-bot-typing">
                  <div className="gw-bot-typing-dot" />
                  <div className="gw-bot-typing-dot" />
                  <div className="gw-bot-typing-dot" />
                </div>
              </div>
            )}

            {showStepOptions && step === 'category' && (
              <div className="gw-bot-options-grid">
                {CATEGORY_OPTIONS.map(opt => (
                  <button key={opt} onClick={() => handleCategorySelect(opt)} className="gw-bot-option-btn">{t("chatbot.categories." + opt)}</button>
                ))}
              </div>
            )}

            {showStepOptions && step === 'driving_license' && (
              <div className="gw-bot-options-grid">
                {DRIVING_LICENSE_OPTIONS.map(opt => (
                  <button key={opt} onClick={() => handleDrivingLicenseSelect(opt)} className="gw-bot-option-btn">{t("chatbot.driving_licenses." + opt)}</button>
                ))}
              </div>
            )}

            {showStepOptions && step === 'nurse_license_status' && (
              <div className="gw-bot-options-grid">
                {NURSE_LICENSE_STATUS_OPTIONS.map(opt => (
                  <button key={opt} onClick={() => handleNurseLicenseStatusSelect(opt)} className="gw-bot-option-btn">{t("chatbot.nurse_license_status." + opt)}</button>
                ))}
              </div>
            )}

            {showStepOptions && step === 'nursing_license_country' && (
              <div className="gw-bot-options-grid">
                {NURSING_LICENSE_COUNTRY_OPTIONS.map(opt => (
                  <button key={opt} onClick={() => handleNursingLicenseCountrySelect(opt)} className="gw-bot-option-btn">{t("chatbot.nursing_license_countries." + opt)}</button>
                ))}
              </div>
            )}

            {showStepOptions && step === 'experience' && (
              <div className="gw-bot-options-grid">
                {copy.experienceOptions.map(opt => (
                  <button key={opt.id} onClick={() => handleExperienceSelect(opt.id, opt.label)} className="gw-bot-option-btn">{opt.label}</button>
                ))}
              </div>
            )}

            {showStepOptions && step === 'gender' && (
              <div className="gw-bot-options-grid">
                {copy.genderOptions.map(opt => (
                  <button key={opt.id} onClick={() => handleGenderSelect(opt.id, opt.label)} className="gw-bot-option-btn">{opt.label}</button>
                ))}
              </div>
            )}

            {showStepOptions && step === 'nationality' && (
              <div className="gw-bot-options-grid">
                {NATIONALITY_OPTIONS.map(opt => (
                  <button key={opt} onClick={() => handleNationalitySelect(opt)} className="gw-bot-option-btn">{t("chatbot.nationalities." + opt)}</button>
                ))}
              </div>
            )}

            {showStepOptions && step === 'religion' && (
              <div className="gw-bot-options-grid">
                {RELIGION_OPTIONS.map(opt => (
                  <button key={opt} onClick={() => handleReligionSelect(opt)} className="gw-bot-option-btn">{t("chatbot.religions." + opt)}</button>
                ))}
              </div>
            )}

            {showStepOptions && step === 'work_type' && (
              <div className="gw-bot-options-grid">
                {copy.workTypeOptions.map(opt => (
                  <button key={opt.id} onClick={() => handleWorkTypeSelect(opt.id, opt.label)} className="gw-bot-option-btn">{opt.label}</button>
                ))}
              </div>
            )}

            {showStepOptions && step === 'living' && (
              <div className="gw-bot-options-grid">
                {copy.livingOptions.map(opt => (
                  <button key={opt.id} onClick={() => handleLivingSelect(opt.id, opt.label)} className="gw-bot-option-btn">{opt.label}</button>
                ))}
              </div>
            )}

            {showStepOptions && step === 'timeline' && (
              <div className="gw-bot-options-grid">
                {copy.timelineOptions.map(opt => (
                  <button key={opt.id} onClick={() => handleTimelineSelect(opt.id, opt.label)} className="gw-bot-option-btn">{opt.label}</button>
                ))}
              </div>
            )}

            {showStepOptions && step === 'notes' && (
              <button onClick={() => { addUserMessage(t("chatbot.skipped_msg")); submitLead(''); }} className="gw-bot-skip-btn">{copy.skip}</button>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="gw-bot-input-area">
            {step === 'phone' ? (
              <div className="gw-bot-phone-input-row">
                <div className="gw-bot-phone-fields">
                  <div className="gw-bot-code-select" ref={codeDropdownRef}>
                    <button onClick={() => setShowCodeDropdown(!showCodeDropdown)} className="gw-bot-code-btn">
                      {COUNTRY_CODES.find(c => c.code === phoneCode)?.flag || '🌍'} {phoneCode} <ChevronDown size={14} />
                    </button>
                    {showCodeDropdown && (
                      <div className="gw-bot-code-dropdown">
                        <div className="gw-bot-search-box">
                          <input
                            type="text"
                            placeholder={t('chatbot.search')}
                            value={searchPhoneCode}
                            onChange={(e) => setSearchPhoneCode(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="gw-bot-code-list">
                          {COUNTRY_CODES.filter(cc => cc.country.toLowerCase().includes(searchPhoneCode.toLowerCase()) || cc.code.includes(searchPhoneCode)).map(cc => (
                            <button
                              key={cc.code + cc.country}
                              className="gw-bot-code-item"
                              onClick={() => { setPhoneCode(cc.code); setShowCodeDropdown(false); setSearchPhoneCode(''); }}
                            >
                              <span>{cc.flag} {cc.country}</span>
                              <span style={{ opacity: 0.6 }}>{cc.code}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <input
                    type="tel"
                    className="gw-bot-input"
                    placeholder={t('chatbot.whatsapp_placeholder')}
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={(e) => e.key === 'Enter' && submitLead()}
                  />
                </div>
                <button onClick={() => submitLead()} disabled={isSubmitting || phoneNumber.length < 6} className="gw-bot-submit-btn">
                  {isSubmitting ? t('chatbot.submitting_btn') : t('chatbot.submit_btn')}
                </button>
              </div>
            ) : step !== 'done' && step !== 'submitting' ? (
              <div className="gw-bot-text-input-wrapper">
                <input
                  type="text"
                  className={`gw-bot-input ${!['other', 'nationality_other', 'driving_license_other', 'nursing_license_country_other', 'notes', 'name'].includes(step) ? 'disabled' : ''}`}
                  placeholder={!['other', 'nationality_other', 'driving_license_other', 'nursing_license_country_other', 'notes', 'name'].includes(step) ? t('chatbot.select_option_placeholder') : t('chatbot.type_here_placeholder')}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleTextSubmit()}
                  disabled={!['other', 'nationality_other', 'driving_license_other', 'nursing_license_country_other', 'notes', 'name'].includes(step)}
                  autoFocus={['other', 'nationality_other', 'driving_license_other', 'nursing_license_country_other', 'notes', 'name'].includes(step)}
                />
                <button 
                  onClick={handleTextSubmit} 
                  disabled={isSubmitting || (!['other', 'nationality_other', 'driving_license_other', 'nursing_license_country_other', 'notes', 'name'].includes(step) && !input.trim())} 
                  className={`gw-bot-send-btn ${!['other', 'nationality_other', 'driving_license_other', 'nursing_license_country_other', 'notes', 'name'].includes(step) ? 'disabled' : ''}`}
                >
                  <Send size={18} />
                </button>
              </div>
            ) : null}

            {step === 'submitting' && <div className="gw-bot-submitting-text">{t('chatbot.submitting')}</div>}
            {step === 'done' && <div className="gw-bot-submitting-text" style={{ color: '#10b981', fontWeight: 600 }}>{t('chatbot.submitted')}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default GulfWorkerLeadBot;
